// Sidebar navigation — restructured 2026-05-24.
//
// Goals of the new structure (see docs/ux-audit-2026-05-24/SIDEBAR_AND_PARSER_REVIEW.md):
//   1. Drop the single flat "Shipment" parent — was 14 mixed children.
//   2. Group entries by job-to-be-done: Data, Operations, Finance & Customers,
//      Inbox (things waiting for me), Administration (superadmin only).
//   3. Daily operator scans 1–2 groups; superadmin/admin tools stop polluting
//      the operator's menu.
//   4. Add a top-level Inbox combining Pending Imports + Chat ID Queue +
//      Claim Parcel with a single badge count.
//
// Section titles use `heading` so the layout renders them as separators.
// Each `requiresSuperadmin: true` entry is also server-gated; the menu
// gate is cosmetic only.

export default [
  // ── Dashboard ─────────────────────────────────────────────────────────
  {
    title: 'Tracking Dashboard',
    icon: { icon: 'tabler-chart-line' },
    to: 'shipment-tracking',
    action: 'read',
    subject: 'Dashboard',
  },

  // ── Inbox (things waiting for me) ─────────────────────────────────────
  // Landing at /shipment/inbox shows a 3-card overview with counts;
  // each child is also directly clickable from the expanded menu.
  {
    title: 'Inbox',
    icon: { icon: 'tabler-inbox' },
    to: 'shipment-inbox',
    action: 'read',
    subject: 'Parcels',

    // Composite badge: total pending across Chat ID Queue + Pending Imports.
    // Layout injector swaps this for a real string via `inboxTotal` key.
    _dynamicBadge: 'inboxTotal',
    children: [
      {
        title: 'Claim Parcel',
        icon: { icon: 'tabler-link' },
        to: 'shipment-claim-parcel',

        // Daily warehouse work — open to anyone with parcel-read perms.
        // Server gates the actual write via role:superadmin|admin|team-manager.
        action: 'read',
        subject: 'Parcels',

        // Count of unattached parcels (customer_id IS NULL, status in
        // pending/received/arrived). The layout swaps this sentinel for
        // the real count from useSidebarBadges.
        _dynamicBadge: 'unclaimedParcels',
      },
      {
        title: 'Chat ID Queue',
        to: 'shipment-customers-identification-queue',
        icon: { icon: 'tabler-id-badge-2' },
        action: 'read',
        subject: 'Customers',
        _dynamicBadge: 'chatIdentityClaims',
      },
      {
        title: 'Pending Imports',
        icon: { icon: 'tabler-file-alert' },
        to: 'shipment-pending-imports',

        // Admin + superadmin only — gated server-side too via role:superadmin|admin.
        action: 'read',
        subject: 'PackingLists',
        _dynamicBadge: 'pendingImports',
      },
    ],
  },

  // ── Data entry ────────────────────────────────────────────────────────
  { heading: 'Data' },
  {
    title: 'Incoming Parcels',
    icon: { icon: 'tabler-package-import' },
    action: 'read',
    subject: 'Parcels',
    children: [
      {
        title: 'List',
        to: 'shipment-incoming-parcels',
        icon: { icon: 'tabler-list' },
        action: 'read',
        subject: 'Parcels',
      },
      {
        title: 'Create',
        to: 'shipment-incoming-parcels-create',
        icon: { icon: 'tabler-plus' },
        action: 'create',
        subject: 'Parcels',
      },
    ],
  },
  {
    title: 'Outgoing Packages',
    icon: { icon: 'tabler-package-export' },
    action: 'read',
    subject: 'Packages',
    children: [
      {
        title: 'List',
        to: 'shipment-outgoing-packages',
        icon: { icon: 'tabler-list' },
        action: 'read',
        subject: 'Packages',
      },
      {
        title: 'Create',
        to: 'shipment-outgoing-packages-create',
        icon: { icon: 'tabler-plus' },
        action: 'create',
        subject: 'Packages',
      },
    ],
  },
  {
    title: 'Packing Lists',
    icon: { icon: 'tabler-clipboard-list' },
    action: 'read',
    subject: 'PackingLists',
    children: [
      {
        title: 'List',
        to: 'shipment-packing-lists',
        icon: { icon: 'tabler-list' },
        action: 'read',
        subject: 'PackingLists',
      },
      {
        title: 'Create',
        to: 'shipment-packing-lists-create',
        icon: { icon: 'tabler-plus' },
        action: 'create',
        subject: 'PackingLists',
      },
    ],
  },
  {
    title: 'Partner Shipments',
    icon: { icon: 'tabler-truck-loading' },
    action: 'create',
    subject: 'Parcels',
    to: 'shipment-partner-shipments',
  },

  // The standalone "Products" CRUD page used to live here. It's a
  // leftover from the older implementation — product data on parcels
  // is now populated by the Excel importer (raw Chinese strings →
  // shipment_products via ShipmentProductLine). No active workflow
  // depends on the standalone list/create UI, so it's hidden from
  // the sidebar 2026-05-24. The underlying model + pages are kept
  // for now in case we resurrect a translation-management surface.

  // ── Warehouse operations ──────────────────────────────────────────────
  { heading: 'Warehouse' },
  {
    title: 'Warehouse Operations',
    icon: { icon: 'tabler-building-warehouse' },
    action: 'read',
    subject: 'Warehouse',
    to: 'shipment-warehouse',
  },

  // Container-wide cotation (replaces the Excel TEST COTATION sheet).
  {
    title: 'Cotation',
    icon: { icon: 'tabler-calculator' },
    action: 'read',
    subject: 'Warehouse',
    to: 'shipment-warehouse-cotation-index',
  },

  // FINAL 7 / PRODUCT LIST / GODANA / GOING generated from the original PL.
  {
    title: 'Fichiers PL',
    icon: { icon: 'tabler-file-spreadsheet' },
    action: 'read',
    subject: 'Warehouse',
    to: 'shipment-warehouse-pl-files-index',
  },

  // Container Arrivals are auto-created by the PL importer from the filename
  // (e.g. 1.23_SEA.xlsx). This entry exists so admin can fix metadata.
  {
    title: 'Container Arrivals',
    icon: { icon: 'tabler-truck' },
    to: 'shipment-container-arrivals',
    action: 'read',
    subject: 'ContainerArrivals',
    requiresAdmin: true,
  },

  // ── Finance & customers ───────────────────────────────────────────────
  { heading: 'Finance & Customers' },
  {
    title: 'Invoices',
    icon: { icon: 'tabler-file-invoice' },
    to: 'shipment-warehouse-invoices',
    action: 'read',
    subject: 'Warehouse',
  },
  {
    title: 'Customers',
    icon: { icon: 'tabler-users' },
    action: 'read',
    subject: 'Customers',
    children: [
      {
        title: 'List',
        to: 'shipment-customers',
        icon: { icon: 'tabler-list' },
        action: 'read',
        subject: 'Customers',
      },
      {
        title: 'Create',
        to: 'shipment-customers-create',
        icon: { icon: 'tabler-plus' },
        action: 'create',
        subject: 'Customers',
      },
    ],
  },
  {
    title: 'History',
    icon: { icon: 'tabler-history' },
    action: 'read',
    subject: 'Analytics',
    children: [
      {
        title: 'Customer History',

        // tabler-users-history does not exist in the bundled icon set;
        // tabler-history-toggle works and visually pairs with the parent
        // tabler-history (rotated arrow) without duplicating it.
        icon: { icon: 'tabler-history-toggle' },
        action: 'read',
        subject: 'Analytics',
        to: 'history-customers',
      },
      {
        title: 'Container History',
        icon: { icon: 'tabler-truck-delivery' },
        action: 'read',
        subject: 'Analytics',
        to: 'history-containers',
      },
      {
        title: 'Reports & Analytics',
        icon: { icon: 'tabler-chart-bar' },
        action: 'read',
        subject: 'Analytics',
        to: 'history-reports',
      },
    ],
  },

  // ── Administration (superadmin only) ──────────────────────────────────
  // User Management used to live in skeleton.js as a sibling; absorbed here
  // so all admin tools sit under one section. The old skeleton.js export is
  // now empty (kept for back-compat with the index.js spread).
  { heading: 'Administration' },
  {
    title: 'User Management',
    icon: { icon: 'tabler-user-cog' },
    action: 'read',
    subject: 'Users',
    requiresSuperadmin: true,
    children: [
      {
        title: 'Users',
        to: 'user-management-users',
        action: 'read',
        subject: 'Users',
        requiresSuperadmin: true,
      },
      {
        title: 'Teams',
        to: 'user-management-teams',
        action: 'read',
        subject: 'Teams',
        requiresSuperadmin: true,
      },
      {
        title: 'Roles',
        to: 'user-management-roles',
        action: 'read',
        subject: 'Roles',
        requiresSuperadmin: true,
      },
      {
        title: 'Permissions',
        to: 'user-management-permissions',
        action: 'read',
        subject: 'Permissions',
        requiresSuperadmin: true,
      },
    ],
  },
  {
    title: 'Conversations',
    icon: { icon: 'tabler-messages' },
    to: 'shipment-admin-conversations',
    action: 'read',
    subject: 'Customers',
    requiresSuperadmin: true,
  },
  {
    title: 'Settings',
    icon: { icon: 'tabler-settings' },
    to: 'shipment-settings',
    action: 'read',
    subject: 'Settings',
    requiresSuperadmin: true,
  },
]
